<template>
  <UgBalloon class="account-balloon" :direction="BalloonDirection.TopEnd">
    <div class="account-balloon__content">
      <div class="account-balloon__top">
        <p class="account-balloon__top-title text-xs">{{ user ? user.client : "" }}</p>
        <h2 class="account-balloon__top-username">{{ user ? user.name : "" }}</h2>
        <p class="account-balloon__top-loginName">{{ user ? user.loginName : "" }}</p>
      </div>

      <div class="account-balloon__bottom">
        <a href="/individual-settings/login-id/" class="account-balloon__bottom-item account-balloon__account-settings">
          <UgButton class="account-balloon__bottom-button" tertiary icon="one-user" :text="t('accountSettings')" />
        </a>

        <a
          v-if="user?.isAdmin"
          href="/client-settings/"
          class="account-balloon__bottom-item account-balloon__account-settings"
        >
          <UgButton class="account-balloon__bottom-button" tertiary icon="settings" :text="t('systemSettings')" />
        </a>

        <div class="account-balloon__bottom-item" @click="logout">
          <UgButton
            data-test="logout-button"
            class="account-balloon__bottom-button"
            tertiary
            icon="logout"
            :text="t('logout')"
          />
        </div>
      </div>
    </div>
  </UgBalloon>
</template>

<script setup lang="ts">
import { UgBalloon, UgButton, BalloonDirection } from "@bebit/atomic";
import { LoginUser, authStore } from "@bebit/auth";
import { Ref, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const user: Ref<LoginUser | undefined> = ref(undefined);

onMounted(async () => {
  user.value = await authStore.getLoginUser();
});

async function logout() {
  try {
    await authStore.logout();
    location.assign("/");
  } catch (e) {
    console.error(e);
  }
}
</script>

<style lang="scss" scoped>
.account-balloon {
  box-shadow: 5px 5px 5px rgba(0, 0, 0, 0.1);
  width: 320px;

  ::before {
    border: 8px solid transparent;
    border-bottom-color: $colorGray600;
    right: 17px;
    top: -16px;
  }

  ::after {
    border: 8px solid transparent;
    border-bottom-color: $colorWhite;
    right: 17px;
    top: -15px;
  }
}

.account-balloon__top {
  align-items: start;
  border-bottom: 1px solid $colorBase500;
  display: flex;
  flex-direction: column;
  padding: 15px;
}

.account-balloon__account-settings {
  text-decoration: none;

  .account-balloon__bottom-button {
    padding: 0;
  }
}
.account-balloon__top-title {
  color: $colorBase900;
  line-height: 1;
  margin-bottom: 7px;
}

.account-balloon__top-username {
  color: $colorBase900;
  line-height: 1;
  margin-bottom: 5px;
}

.account-balloon__top-loginName {
  color: $colorBase900;
  line-height: 1;
}

.account-balloon__bottom {
  align-items: start;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
}

.account-balloon__bottom-item {
  align-items: center;
  background-color: $colorWhite;
  color: $colorBase900;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: start;
  padding-left: 22px;
  padding-right: 22px;
  width: 100%;

  :deep(.ug-button) {
    padding: 0;
  }

  :deep(.ug-button__text) {
    font-size: 14px;
    line-height: 1.3;
    margin-left: 8px;
  }

  :deep(.account-balloon__bottom-button .ug-button__icon path) {
    fill: $colorBase700;
  }

  &:hover {
    background-color: $v1ColorBlue600;
    text-decoration: none;

    :deep(.account-balloon__bottom-button .ug-button__icon path, .ug-button__icon path) {
      fill: $colorBase900;
    }
  }
}
</style>

<i18n lang="yaml">
en:
  logout: Logout
  accountSettings: My account settings
  systemSettings: System Settings
ja:
  logout: ログアウト
  accountSettings: 個人アカウント設定
  systemSettings: システム設定
zh-hant:
  logout: 註銷
  accountSettings: 個人賬戶設置
  systemSettings: 系統設定
zh-hans:
  logout: 注销
  accountSettings: 个人账户设置
  systemSettings: 系统设置
</i18n>
